import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { dashboardStyles, flightMarkStyles, windowsSelectFix } from './dashboard-styles';

export interface DataColumn<T> {
  key: string;
  label: string;
  numeric?: boolean;
  /** Checklist filter for a short set of values. Everything else is a text box. */
  filter?: 'text' | 'select';
  emphasis?: boolean;
  value: (row: T) => string;
  sortValue?: (row: T) => string | number;
  sub?: (row: T) => string;
  /** good = retrievable, bad = blocked, unknown = not checked. */
  mark?: (row: T) => 'good' | 'bad' | 'unknown' | '';
  /** Custom cell. Button clicks inside it should stop propagation so the row does not open. */
  cell?: (row: T) => TemplateResult | string;
  /** Shown beside the cell on hover. */
  tip?: (row: T) => TemplateResult | string;
}

@customElement('fp3-data-table')
export class Fp3DataTable extends LitElement {
  static styles = [
    dashboardStyles,
    flightMarkStyles,
    windowsSelectFix,
    css`
      :host {
        display: block;
        width: 100%;
        font-family: var(--dash-font, inherit);
        color: var(--dash-text, inherit);
      }
      :host([fill]) .table {
        height: calc(100dvh - 8rem);
        max-height: calc(100dvh - 8rem);
      }
      *, *::before, *::after { box-sizing: border-box; }
      tbody tr {
        cursor: pointer;
        content-visibility: auto;
        contain-intrinsic-size: auto 44px;
      }
      tr.selected td { background: var(--dash-accent-soft); }
      .tail { color: var(--dash-accent); font-weight: 600; }
      .muted { color: var(--dash-muted); font-size: 0.75rem; margin-top: 0.15rem; }
      .reach { display: grid; gap: 0.1rem; }
      .reach-actions { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.3rem; }
      .reach button {
        font: inherit; font-size: 0.72rem; font-weight: 600; padding: 0.2rem 0.45rem;
        border-radius: 6px; border: 1px solid var(--dash-border, #314255);
        background: transparent; color: var(--dash-accent, #5b9fd4); cursor: pointer;
      }
      td.has-tip { cursor: help; }
      select.stage-select {
        font: inherit; font-size: 0.78rem; color: inherit;
        background: transparent; border: 1px solid var(--dash-border, #314255);
        border-radius: 6px; padding: 0.15rem 0.3rem; max-width: 9.5rem; cursor: pointer;
      }
      .col-tip {
        position: fixed;
        z-index: 40;
        width: min(360px, calc(100vw - 16px));
        padding: 8px 10px;
        border: 1px solid var(--dash-border, #314255);
        border-radius: 8px;
        background: var(--dash-elevated, #182430);
        color: var(--dash-text, #e8edf2);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
        pointer-events: none;
        font-size: 0.78rem;
        line-height: 1.4;
        transform: translateY(-100%);
      }
      .col-tip.below { transform: none; }
      .tip-head { font-weight: 600; margin-bottom: 0.35rem; }
      .empty { text-align: center; color: var(--dash-muted); padding: 2rem 1rem; cursor: default; }
      .empty:hover { background: transparent; }
    `,
  ];

  @property({ type: Array }) columns: DataColumn<Record<string, unknown>>[] = [];
  @property({ type: Array }) rows: Record<string, unknown>[] = [];
  @property() keyField = 'id';
  @property() selectedKey: string | number = '';
  @property() empty = 'No rows match.';
  @property({ type: Boolean, reflect: true }) fill = false;

  @state() private sortKey = '';
  @state() private sortDir: 'asc' | 'desc' = 'asc';
  @state() private filtersOpen = false;
  @state() private textFilters: Record<string, string> = {};
  @state() private selectFilters: Record<string, string[]> = {};
  @state() private menu: { key: string; top: number; left: number; width: number } | null = null;
  @state() private hoverTip: { body: TemplateResult | string; top: number; left: number; below: boolean } | null = null;

  private textOf(column: DataColumn<Record<string, unknown>>, row: Record<string, unknown>) {
    return column.value(row) || '';
  }

  private columnActive(column: DataColumn<Record<string, unknown>>) {
    if (column.filter === 'select') return (this.selectFilters[column.key] || []).length > 0;
    return Boolean((this.textFilters[column.key] || '').trim());
  }

  private get filteredRows() {
    const matched = this.rows.filter((row) =>
      this.columns.every((column) => {
        const text = this.textOf(column, row);
        if (column.filter === 'select') {
          const picked = this.selectFilters[column.key] || [];
          return picked.length === 0 || picked.includes(text);
        }
        const query = (this.textFilters[column.key] || '').trim().toLowerCase();
        return !query || text.toLowerCase().includes(query);
      }),
    );
    if (!this.sortKey) return matched;
    const column = this.columns.find((item) => item.key === this.sortKey);
    if (!column) return matched;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...matched].sort((a, b) => {
      const av = column.sortValue ? column.sortValue(a) : this.textOf(column, a);
      const bv = column.sortValue ? column.sortValue(b) : this.textOf(column, b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }

  private optionsFor(column: DataColumn<Record<string, unknown>>) {
    const values = new Set<string>();
    for (const row of this.rows) {
      const text = this.textOf(column, row);
      if (text) values.add(text);
    }
    return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  private toggleFilters() {
    this.filtersOpen = !this.filtersOpen;
    if (!this.filtersOpen) this.menu = null;
  }

  private sortBy(key: string) {
    if (this.sortKey !== key) {
      this.sortKey = key;
      this.sortDir = 'asc';
      return;
    }
    if (this.sortDir === 'asc') {
      this.sortDir = 'desc';
      return;
    }
    this.sortKey = '';
  }

  private setText(key: string, value: string) {
    this.textFilters = { ...this.textFilters, [key]: value };
  }

  private toggleOption(key: string, option: string) {
    const current = this.selectFilters[key] || [];
    const next = current.includes(option) ? current.filter((item) => item !== option) : [...current, option];
    this.selectFilters = { ...this.selectFilters, [key]: next };
  }

  private clearFilters() {
    this.textFilters = {};
    this.selectFilters = {};
    this.menu = null;
  }

  private openMenu(event: Event, key: string) {
    const trigger = event.currentTarget as HTMLElement;
    const box = trigger.getBoundingClientRect();
    this.menu = this.menu?.key === key ? null : { key, top: box.bottom + 6, left: box.left, width: Math.max(box.width, 180) };
  }

  private selectLabel(column: DataColumn<Record<string, unknown>>) {
    const picked = this.selectFilters[column.key] || [];
    if (picked.length === 0) return 'All';
    if (picked.length === 1) return picked[0];
    return `${picked.length} selected`;
  }

  private showTip(event: MouseEvent, column: DataColumn<Record<string, unknown>>, row: Record<string, unknown>) {
    const body = column.tip?.(row);
    if (body === undefined || body === '') {
      this.hoverTip = null;
      return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const below = rect.top < 180;
    this.hoverTip = {
      body,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 376)),
      top: below ? rect.bottom + 8 : rect.top - 8,
      below,
    };
  }

  private choose(row: Record<string, unknown>) {
    this.dispatchEvent(new CustomEvent('select', { detail: row, bubbles: true, composed: true }));
  }

  private sortGlyph(key: string) {
    if (this.sortKey !== key) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  render() {
    const shown = this.filteredRows;
    const filtering = this.columns.some((column) => this.columnActive(column));
    return html`
      <div class="dt-wrap">
        <div class="dt-count">
          <span>${shown.length.toLocaleString()} of ${this.rows.length.toLocaleString()}</span>
          ${filtering
            ? html`<button type="button" class="dt-clear" @click=${this.clearFilters}>Clear filters</button>`
            : nothing}
        </div>
        <div class="table" @scroll=${() => { this.hoverTip = null; }}>
          <table class="data-table">
            <thead>
              <tr>
                ${this.columns.map((column) => html`
                  <th
                    class="th ${column.numeric ? 'num' : ''} ${this.sortKey === column.key ? 'sorted' : ''} ${this.columnActive(column) ? 'filtered' : ''}"
                    aria-sort=${this.sortKey === column.key ? (this.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div class="th-top">
                      <span class="th-label" @click=${this.toggleFilters}>${column.label}</span>
                      <button
                        type="button"
                        class="th-sort ${this.sortKey === column.key ? 'active' : ''}"
                        aria-label="Sort by ${column.label}"
                        @click=${() => this.sortBy(column.key)}
                      >${this.sortGlyph(column.key)}</button>
                      <button
                        type="button"
                        class="col-filter-btn ${this.columnActive(column) ? 'active' : ''}"
                        aria-label="Filter ${column.label}"
                        aria-expanded=${this.filtersOpen}
                        @click=${this.toggleFilters}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                          <path fill="currentColor" d="M1.5 2h13l-5 6.1V14l-3-1.4V8.1L1.5 2z"></path>
                        </svg>
                      </button>
                    </div>
                  </th>
                `)}
              </tr>
              ${this.filtersOpen
                ? html`
                  <tr class="filter-row">
                    ${this.columns.map((column) => html`
                      <td>
                        <div class="col-filter-cell">
                          ${column.filter === 'select' ? this.renderSelect(column) : html`
                            <input
                              class="col-filter-in"
                              type="text"
                              placeholder=${column.label}
                              .value=${this.textFilters[column.key] || ''}
                              @input=${(event: Event) => this.setText(column.key, (event.target as HTMLInputElement).value)}
                            />
                          `}
                        </div>
                      </td>
                    `)}
                  </tr>
                `
                : nothing}
            </thead>
            <tbody>
              ${shown.length
                ? repeat(
                    shown,
                    (row) => String(row[this.keyField]),
                    (row) => html`
                      <tr
                        class=${String(row[this.keyField]) === String(this.selectedKey) ? 'selected' : ''}
                        @click=${() => this.choose(row)}
                      >
                        ${this.columns.map((column) => {
                          const custom = column.cell?.(row);
                          const text = this.textOf(column, row);
                          const sub = column.sub?.(row);
                          const tone = column.mark?.(row) || '';
                          const body = column.emphasis
                            ? html`<span class="tail">${text || '—'}</span>`
                            : text || '—';
                          return html`
                            <td
                              class=${`${column.numeric ? 'num' : ''} ${column.tip ? 'has-tip' : ''}`.trim()}
                              @mouseenter=${(event: MouseEvent) => this.showTip(event, column, row)}
                              @mouseleave=${() => { this.hoverTip = null; }}
                            >
                              ${custom !== undefined
                                ? custom
                                : tone
                                  ? html`<span class="flight-mark ${tone}"><span class="flight-dot" aria-hidden="true"></span>${body}</span>`
                                  : body}
                              ${custom !== undefined || !sub ? nothing : html`<div class="muted">${sub}</div>`}
                            </td>
                          `;
                        })}
                      </tr>
                    `,
                  )
                : html`<tr class="empty"><td class="empty" colspan=${this.columns.length}>${this.empty}</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>
      ${this.menu ? this.renderMenu() : nothing}
      ${this.hoverTip
        ? html`<div class="col-tip ${this.hoverTip.below ? 'below' : ''}" style="top:${this.hoverTip.top}px;left:${this.hoverTip.left}px">${this.hoverTip.body}</div>`
        : nothing}
    `;
  }

  private renderSelect(column: DataColumn<Record<string, unknown>>) {
    const open = this.menu?.key === column.key;
    return html`
      <div class="col-select">
        <button type="button" class="col-select-trigger" @click=${(event: Event) => this.openMenu(event, column.key)}>
          <span>${this.selectLabel(column)}</span>
          <span class="col-select-arrow ${open ? 'open' : ''}">▼</span>
        </button>
      </div>
    `;
  }

  private renderMenu() {
    const menu = this.menu;
    if (!menu) return nothing;
    const column = this.columns.find((item) => item.key === menu.key);
    if (!column) return nothing;
    const picked = this.selectFilters[column.key] || [];
    return html`
      <div class="col-select-backdrop" @click=${() => { this.menu = null; }}></div>
      <div
        class="col-select-menu"
        style=${styleMap({ top: `${menu.top}px`, left: `${menu.left}px`, width: `${menu.width}px` })}
      >
        <div class="col-select-actions">
          <button
            type="button"
            class="multi-select-clear"
            @click=${() => { this.selectFilters = { ...this.selectFilters, [column.key]: [] }; }}
          >Clear</button>
        </div>
        <div class="col-select-options">
          ${this.optionsFor(column).map((option) => html`
            <label class="multi-select-option">
              <input
                type="checkbox"
                .checked=${picked.includes(option)}
                @change=${() => this.toggleOption(column.key, option)}
              />
              <span>${option}</span>
            </label>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-data-table': Fp3DataTable;
  }
}
